import React from 'react';
import { activity } from '@codametrix/ui-common';
import { userActivityFormatter } from './useractivity-timeline-formatter';
import { isIe11 } from '@braintree/browser-detection';
import {
  cqaActivityFormatter,
  systemActivityFormatter
} from './systemactivity-timeline-formatter';
import { UserActivityComment } from '../userActivityComment/userActivityComment';
import { cqaUserActivityFeedbackFormatter, cqaUserActivityFormatter, userActivityCommentFormatter } from './useractivityComment-timeline-formatter';
import { isMatchingComment } from '../../utils/activity-utils';
import './styles/index.scss';

export type TimelineProps = {
  items: (CMxAPI.UserActivity | CMxAPI.SystemActivity | CQA.ActivityEntry | CMxAPI.UserActivityComment | CQA.CQAComment)[];
  enableUserActivityComments?:boolean;
  editable?:boolean;
  filter?: "comments" | "all" | "feedback";
  setTimelineFilter?: (filter: "comments" | "all" | "feedback") => void;
};

enum ActivityType {
  USER = 'USER',
  SYSTEM = 'SYSTEM',
  CQA = 'CQA',
  USER_ACTIVITY_COMMENTS = 'USER_ACTIVITY_COMMENTS',
  CQA_CODER_COMMENT = 'CQA_CODER_COMMENT',
  CQA_FEEDBACK_COMMENT = 'CQA_FEEDBACK_COMMENT',
}

const Timeline: React.FC<TimelineProps> = ({ items: allItems, enableUserActivityComments, editable, filter: externalFilter, setTimelineFilter }) => {
  const [filter, setFilter] = React.useState<"feedback" | "comments" | "all">(externalFilter ?? "all");

  React.useEffect(() => {
    if (externalFilter !== undefined) {
      setFilter(externalFilter);
    }
  }, [externalFilter]);
  const filteredSourceItems = React.useMemo(() => {
    if (filter === "all") return allItems;
    if (filter === "feedback") {
      return allItems.filter(item => isMatchingComment(item as any, 'CQA_FEEDBACK_COMMENT'));
    }
    return allItems.filter((item: any) => {
      const type = item.activityType || item.type || "";
      return typeof type === "string" && (type === ActivityType.USER_ACTIVITY_COMMENTS || type === ActivityType.CQA_CODER_COMMENT);
    });
  }, [allItems, filter]);

  const raw = React.useMemo(
    () =>
      activity.convertActivityToTimeline(filteredSourceItems, {
        [ActivityType.USER]: userActivityFormatter,
        [ActivityType.SYSTEM]: systemActivityFormatter,
        [ActivityType.CQA]: cqaActivityFormatter,
        [ActivityType.USER_ACTIVITY_COMMENTS]: userActivityCommentFormatter,
        [ActivityType.CQA_CODER_COMMENT]: cqaUserActivityFormatter,
        [ActivityType.CQA_FEEDBACK_COMMENT]: cqaUserActivityFeedbackFormatter,
      }),
    [filteredSourceItems]
  );
    
  if (raw?.length === 0 && !enableUserActivityComments) {
    return <div className="empty-message">No activity.</div>;
  }

  const items = raw.map((item, itemIdx) => {
    const tiles = item.tiles.map((tile, idx) => {
      const subTitle = tile.subtitle ? (
        <p className="cmx-tile-subtitle">On {tile.subtitle}:</p>
      ) : (
        false
      );
      const titles = !tile.titles?.length
        ? false
        : tile.titles.map((title, titleIdx) => (
            <div key={titleIdx} className="cmx-tile-title">
              {title}
            </div>
          ));

      return (
        <div className="cmx-tile" key={idx}>
          <div className="cmx-tile-content">
            {subTitle}
            {titles}
          </div>
        </div>
      );
    });

    return (
      <div
        className="timeline-item"
        data-testid={`timeline-item-${itemIdx}`}
        key={itemIdx}>
        <div className="timeline-left">
          <a className="timeline-icon"></a>
        </div>
        <div className="timeline-content">{tiles}</div>
      </div>
    );
  });

  const handleFilterChange = (selected: "comments" | "all" | "feedback") => {
    setFilter(selected);
    setTimelineFilter?.(selected);
  };

  return (
    <>
       {enableUserActivityComments && (
             <UserActivityComment
                onFilterChange={handleFilterChange}
                editable={editable}
                activeFilter={filter}
             />
        )}
             
    <div className="cmx-timeline timeline">
      <div className="timeline-meta">
        {isIe11()
          ? 'All times listed in UTC'
          : 'All times listed are in your timezone'}
      </div>
      {items}
    </div>
    </>
  );
};
Timeline.displayName = 'Timeline';
export { Timeline };
